// v49 RRHH — registrar una accion de personal (D5).
// Unico camino para cambiar puesto, salario, jornada, jefatura, departamento o
// tipo de contrato: el PUT del expediente responde 409 sobre esos campos.
// Lo usan el detalle del expediente y la bandeja de acciones.

import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../config/api';
import {
  TIPOS_ACCION, LISTA_POR_TIPO, CAMPO_POR_TIPO, TIPOS_CONTRATO, msgError,
  btnSecundario, btnNavy, fmtMonto
} from '../utils/rrhh';
import { Campo, Aviso } from './RrhhCampos';

// borrador: accion existente en estado Borrador que se esta corrigiendo (A10).
// Sin borrador, el modal crea una nueva.
export default function ModalAccionPersonal({ colaborador, listas = {}, colaboradores = [], tipoInicial = 'Puesto', borrador = null, onClose, onGuardado }) {
  const [tipo, setTipo]         = useState(borrador?.tipo || tipoInicial);
  const [valorNuevo, setValor]  = useState(borrador?.valor_nuevo ?? null);
  const [rigeDesde, setRige]    = useState(borrador?.rige_desde ? String(borrador.rige_desde).slice(0, 10) : '');
  const [motivo, setMotivo]     = useState(borrador?.motivo || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError]       = useState('');

  // Valor vigente del expediente, en texto, para dejar constancia del "antes".
  const valorAnterior = () => {
    const campo = CAMPO_POR_TIPO[tipo];
    if (!campo) return null;
    if (tipo === 'Salario') return colaborador.salario != null ? String(colaborador.salario) : null;
    return colaborador[campo.texto] ?? null;
  };

  // Opciones del valor nuevo segun el tipo (spec §6.5).
  const opcionesValor = () => {
    if (tipo === 'Jefatura') {
      return colaboradores
        .filter(c => c.id !== colaborador.id)          // nadie es su propia jefatura
        .map(c => ({ id: c.id, valor: c.nombre_completo }));
    }
    if (tipo === 'Contrato') {
      return TIPOS_CONTRATO.map(t => ({ id: t, valor: t }));
    }
    const lista = LISTA_POR_TIPO[tipo];
    return lista ? (listas[lista] || []) : null;      // null => input libre (Salario)
  };

  const cambiarTipo = (t) => { setTipo(t); setValor(null); setError(''); };

  const guardar = async () => {
    if (valorNuevo == null || valorNuevo === '') { setError('Indica el valor nuevo de la accion.'); return; }
    setGuardando(true); setError('');
    try {
      // valor_anterior NO se manda: lo calcula el backend leyendo el valor
      // vigente del expediente (D-3). El historial es el registro formal de lo
      // que paso; dictarselo desde el navegador lo volveria un dato de confianza.
      const cuerpo = {
        colaborador_id: colaborador.id,
        tipo,
        valor_nuevo: valorNuevo,
        rige_desde: rigeDesde || null,
        motivo: motivo || null
      };
      const r = borrador
        ? await axios.put(`${API}/rrhh-acciones-personal?id=${borrador.id}`, cuerpo)
        : await axios.post(`${API}/rrhh-acciones-personal`, cuerpo);
      onGuardado(r.data);   // { ok, id, estado: 'Borrador' }
    } catch (e) { setError(msgError(e)); setGuardando(false); }
  };

  const opciones = opcionesValor();
  const anterior = valorAnterior();

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, overflow: 'hidden' }}>
        <div style={{ background: '#012E40', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>
            {borrador ? 'Corregir borrador de accion' : 'Registrar accion de personal'}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>&#10005;</button>
        </div>

        <div style={{ padding: 24 }}>
          <Aviso texto={error} />

          <div style={{ fontSize: 13, color: '#012E40', fontWeight: 600, marginBottom: 4 }}>
            {colaborador.codigo} · {colaborador.nombre_completo}
          </div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 18 }}>
            Se guarda como <b>Borrador</b>: podes corregirla o descartarla. Al enviarla desde la bandeja
            arranca la doble aprobacion y ya no se edita. El expediente no cambia hasta la aprobacion final.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Campo label="Tipo de accion" opciones={TIPOS_ACCION.map(t => ({ id: t, valor: t }))}
              valor={tipo} onChange={cambiarTipo} />

            <div>
              <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Valor actual</div>
              <div style={{ fontSize: 13, color: '#012E40', marginTop: 8 }}>
                {tipo === 'Salario'
                  ? fmtMonto(anterior, colaborador.moneda)
                  : (anterior || <span style={{ color: '#ccc' }}>—</span>)}
              </div>
            </div>

            {opciones
              ? <Campo label="Valor nuevo" requerido opciones={opciones} valor={valorNuevo} onChange={setValor} />
              : <Campo label="Valor nuevo" requerido tipo="number" valor={valorNuevo} onChange={setValor} />}

            <Campo label="Rige desde" tipo="date" valor={rigeDesde} onChange={setRige} />
            <div style={{ gridColumn: '1/-1' }}>
              <Campo label="Motivo" valor={motivo} onChange={setMotivo} placeholder="Queda en el historial del expediente" />
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btnSecundario}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} style={{ ...btnNavy, opacity: guardando ? 0.7 : 1 }}>
            {guardando ? 'Guardando...' : (borrador ? 'Guardar borrador' : 'Registrar accion')}
          </button>
        </div>
      </div>
    </div>
  );
}
